function normalizeNullableString(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function uniqueStrings(values: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  const output: string[] = [];
  for (const value of values) {
    const normalized = normalizeNullableString(value);
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    output.push(normalized);
  }
  return output;
}

const TERMINAL_STAGES = new Set(["closed_onboarding", "ngmi", "no_token"]);
const PROJECT_STAGE_DEFAULT_OFFSETS: Partial<Record<string, number>> = {
  lead: 3,
  group_created: 2,
  call_completed: 2,
  passed_ic: 2,
  qualified_for_sale: 3,
  contract_shared: 2,
  no_for_now: 90, // Follow up in ~3 months
};
const INTRO_STATUS_OFFSETS: Partial<Record<string, number>> = {
  not_asked: 0,
  follow_up_needed: 1,
  asked: 2,
  no_response: 3,
};
const INTRO_STATUS_PRIORITY: Record<string, number> = {
  not_asked: 0,
  follow_up_needed: 1,
  asked: 2,
  no_response: 3,
};

export interface EvPolicyWorkflowLike {
  stage: string;
  nextAction: string | null;
  ownerUid: string | null;
  ownerEmail: string | null;
  callReadyStatus: string;
  nextTouchpointType: string;
  nextTouchpointAt: string | null;
  projectFollowUpDate?: string | null;
  warmIntroFollowUpDate?: string | null;
  followUpDate?: string | null;
}

export interface EvPolicyWarmLeadLike {
  hasWarmLead: boolean;
  untouched: boolean;
}

export interface EvPolicyHandoffLike {
  status: string;
}

export interface EvPolicyIntroContactLike {
  fundName: string;
  status: string;
  isCurrentWarmMatch?: boolean;
  lastStatusChangedAt?: string | null;
  contact: {
    name: string;
  };
}

export interface EvPolicyFlags {
  overdue: boolean;
  dueToday: boolean;
  projectFollowUpOverdue: boolean;
  projectFollowUpDueToday: boolean;
  warmIntroFollowUpOverdue: boolean;
  warmIntroFollowUpDueToday: boolean;
  touchpointToday: boolean;
  callToday: boolean;
  missingNextAction: boolean;
  unassigned: boolean;
  callPrepActive: boolean;
  readyForHandoff: boolean;
  handoffFailed: boolean;
  handoffQueued: boolean;
  closed: boolean;
  hasWarmLead: boolean;
  unworkedWarmLead: boolean;
  prioritySignals: string[];
}

export interface WarmIntroFollowUpLane {
  warmIntroFollowUpDate: string | null;
  warmIntroFollowUpReason: string | null;
  prioritySignals: string[];
}

export function currentDateOnly(now = new Date()): string {
  return now.toISOString().slice(0, 10);
}

export function toIsoDateOnly(value: string | null | undefined): string | null {
  if (!value) return null;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value.slice(0, 10);
  }

  return parsed.toISOString().slice(0, 10);
}

export function compareDateOnly(left: string | null, right: string | null): number {
  if (left === right) return 0;
  if (left == null) return 1;
  if (right == null) return -1;
  return left.localeCompare(right);
}

export function compareDateTimeDay(value: string | null, targetDay: string): number | null {
  const normalized = toIsoDateOnly(value);
  if (!normalized) return null;
  return normalized.localeCompare(targetDay);
}

export function addDaysToDateOnly(dateOnly: string, days: number): string {
  const parsed = new Date(`${dateOnly}T00:00:00.000Z`);
  parsed.setUTCDate(parsed.getUTCDate() + days);
  return parsed.toISOString().slice(0, 10);
}

export function isTerminalWorkflowStage(stage: string): boolean {
  return TERMINAL_STAGES.has(stage);
}

export function isActiveCallReadyStatus(status: string): boolean {
  return status !== "idle";
}

export function getCanonicalProjectFollowUpDate(
  workflow: Pick<EvPolicyWorkflowLike, "projectFollowUpDate" | "followUpDate">
): string | null {
  return toIsoDateOnly(workflow.projectFollowUpDate ?? workflow.followUpDate ?? null);
}

export function getEarliestFollowUpDate(
  ...values: Array<string | null | undefined>
): string | null {
  return (
    values
      .map((value) => toIsoDateOnly(value ?? null))
      .filter((value): value is string => Boolean(value))
      .sort((left, right) => left.localeCompare(right))[0] ?? null
  );
}

export function defaultProjectFollowUpDateForStage(
  stage: string,
  nowDateOnly = currentDateOnly()
): string | null {
  const offset = PROJECT_STAGE_DEFAULT_OFFSETS[stage];
  if (offset == null) return null;
  return addDaysToDateOnly(nowDateOnly, offset);
}

export function deriveWarmIntroFollowUpLane(
  contacts: EvPolicyIntroContactLike[],
  nowDateOnly = currentDateOnly()
): WarmIntroFollowUpLane {
  const scope = contacts.some((contact) => contact.isCurrentWarmMatch)
    ? contacts.filter((contact) => contact.isCurrentWarmMatch)
    : contacts;

  const actionable = scope
    .map((contact) => {
      const offset = INTRO_STATUS_OFFSETS[contact.status];
      if (offset == null) return null;
      return {
        contact,
        dueDate: addDaysToDateOnly(nowDateOnly, offset),
        priority: INTRO_STATUS_PRIORITY[contact.status] ?? 99,
      };
    })
    .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
    .sort((left, right) => {
      const dateDelta = left.dueDate.localeCompare(right.dueDate);
      if (dateDelta !== 0) return dateDelta;

      const priorityDelta = left.priority - right.priority;
      if (priorityDelta !== 0) return priorityDelta;

      const leftChanged = left.contact.lastStatusChangedAt ?? "";
      const rightChanged = right.contact.lastStatusChangedAt ?? "";
      if (leftChanged !== rightChanged) {
        return leftChanged.localeCompare(rightChanged);
      }

      return `${left.contact.fundName}:${left.contact.contact.name}`.localeCompare(
        `${right.contact.fundName}:${right.contact.contact.name}`
      );
    });

  const selected = actionable[0];
  if (!selected) {
    return {
      warmIntroFollowUpDate: null,
      warmIntroFollowUpReason: null,
      prioritySignals: [],
    };
  }

  const name = `${selected.contact.contact.name} @ ${selected.contact.fundName}`;
  switch (selected.contact.status) {
    case "not_asked":
      return {
        warmIntroFollowUpDate: selected.dueDate,
        warmIntroFollowUpReason: `Start warm intro outreach with ${name}.`,
        prioritySignals: ["warm_intro_not_asked"],
      };
    case "follow_up_needed":
      return {
        warmIntroFollowUpDate: selected.dueDate,
        warmIntroFollowUpReason: `Follow up with ${name}.`,
        prioritySignals: ["warm_intro_follow_up_needed"],
      };
    case "asked":
      return {
        warmIntroFollowUpDate: selected.dueDate,
        warmIntroFollowUpReason: `Check whether ${name} responded to the intro ask.`,
        prioritySignals: ["warm_intro_waiting_response"],
      };
    case "no_response":
      return {
        warmIntroFollowUpDate: selected.dueDate,
        warmIntroFollowUpReason: `Nudge ${name} after no response.`,
        prioritySignals: ["warm_intro_no_response"],
      };
    default:
      return {
        warmIntroFollowUpDate: null,
        warmIntroFollowUpReason: null,
        prioritySignals: [],
      };
  }
}

export function deriveIntroMadeProjectFollowUpDate(
  contacts: EvPolicyIntroContactLike[],
  existingProjectFollowUpDate: string | null,
  nowDateOnly = currentDateOnly()
): string | undefined {
  const currentMatches = contacts.some((contact) => contact.isCurrentWarmMatch)
    ? contacts.filter((contact) => contact.isCurrentWarmMatch)
    : contacts;
  const hasIntroMade = currentMatches.some((contact) => contact.status === "intro_made");
  if (!hasIntroMade) return undefined;

  const recommended = addDaysToDateOnly(nowDateOnly, 2);
  if (!existingProjectFollowUpDate || existingProjectFollowUpDate.localeCompare(recommended) > 0) {
    return recommended;
  }

  return undefined;
}

export function deriveWorkflowFlags(input: {
  workflow: EvPolicyWorkflowLike;
  projectIsArchived: boolean;
  warmLead: EvPolicyWarmLeadLike;
  latestHandoff: EvPolicyHandoffLike | null;
}): EvPolicyFlags {
  const today = currentDateOnly();
  const active = !input.projectIsArchived && !isTerminalWorkflowStage(input.workflow.stage);
  const projectFollowUpDate = getCanonicalProjectFollowUpDate(input.workflow);
  const warmIntroFollowUpDate = toIsoDateOnly(input.workflow.warmIntroFollowUpDate ?? null);
  const projectFollowUpOverdue =
    active && projectFollowUpDate !== null && compareDateOnly(projectFollowUpDate, today) < 0;
  const projectFollowUpDueToday =
    active && projectFollowUpDate !== null && compareDateOnly(projectFollowUpDate, today) === 0;
  const warmIntroFollowUpOverdue =
    active && warmIntroFollowUpDate !== null && compareDateOnly(warmIntroFollowUpDate, today) < 0;
  const warmIntroFollowUpDueToday =
    active && warmIntroFollowUpDate !== null && compareDateOnly(warmIntroFollowUpDate, today) === 0;
  const touchpointToday =
    active && compareDateTimeDay(input.workflow.nextTouchpointAt, today) === 0;
  const callToday = touchpointToday && input.workflow.nextTouchpointType === "call";
  const readyForHandoff =
    input.workflow.stage === "contract_shared" || input.workflow.stage === "closed_onboarding";
  const handoffFailed = input.latestHandoff?.status === "failed";
  const handoffQueued =
    input.latestHandoff?.status === "queued" || input.latestHandoff?.status === "processing";
  const prioritySignals = uniqueStrings([
    projectFollowUpOverdue ? "project_follow_up_overdue" : null,
    warmIntroFollowUpOverdue ? "warm_intro_follow_up_overdue" : null,
    projectFollowUpDueToday ? "project_follow_up_due_today" : null,
    warmIntroFollowUpDueToday ? "warm_intro_follow_up_due_today" : null,
    callToday ? "call_today" : null,
    active && input.warmLead.hasWarmLead && input.warmLead.untouched ? "warm_lead_untouched" : null,
    active && !normalizeNullableString(input.workflow.nextAction) ? "missing_next_action" : null,
    !normalizeNullableString(input.workflow.ownerEmail) &&
    !normalizeNullableString(input.workflow.ownerUid)
      ? "unassigned"
      : null,
    handoffFailed ? "handoff_failed" : null,
    readyForHandoff ? "ready_for_handoff" : null,
    active && isActiveCallReadyStatus(input.workflow.callReadyStatus) ? "call_prep_active" : null,
  ]);

  return {
    overdue: projectFollowUpOverdue || warmIntroFollowUpOverdue,
    dueToday: projectFollowUpDueToday || warmIntroFollowUpDueToday,
    projectFollowUpOverdue,
    projectFollowUpDueToday,
    warmIntroFollowUpOverdue,
    warmIntroFollowUpDueToday,
    touchpointToday,
    callToday,
    missingNextAction: active && !normalizeNullableString(input.workflow.nextAction),
    unassigned:
      !normalizeNullableString(input.workflow.ownerEmail) &&
      !normalizeNullableString(input.workflow.ownerUid),
    callPrepActive: active && isActiveCallReadyStatus(input.workflow.callReadyStatus),
    readyForHandoff,
    handoffFailed,
    handoffQueued,
    closed: input.workflow.stage === "closed_onboarding",
    hasWarmLead: input.warmLead.hasWarmLead,
    unworkedWarmLead: active && input.warmLead.untouched,
    prioritySignals,
  };
}
